// Friendly, user-facing copy for failures of the live Adyen online-pricing
// resync the editBusiness Save runs when onlinePricing is edited for a merchant
// live on Adyen (PC-638 backend).
//
// We key off the machine-readable `code` when the backend sends one, fall back
// to a message pattern for the validation backstop, and to a generic "not
// saved" message otherwise. Raw Adyen/internal text is never surfaced.

export const GENERIC_ONLINE_PRICING_SYNC_ERROR =
  "Couldn't update the merchant's online pricing in Adyen. The change was not saved. Please try again; if it persists, contact engineering.";

// Adyen resync did not apply, so nothing was saved. PUT /pricing names these as
// AdyenSplitSyncInvariant / ADYEN_SPLIT_CONFIG_NO_RULES; editBusiness still uses
// ADYEN_ONLINE_PRICING_SYNC_FAILED. MISCONFIGURATION is missing Adyen env config.
const ADYEN_SYNC_ERROR_CODES = new Set<string>([
  "ADYEN_ONLINE_PRICING_SYNC_FAILED",
  "MISCONFIGURATION",
  "AdyenSplitSyncInvariant",
  "ADYEN_SPLIT_CONFIG_NO_RULES",
]);

const SYNC_ERROR_MESSAGES: Record<string, string> = {
  ADYEN_ONLINE_PRICING_SYNC_FAILED: GENERIC_ONLINE_PRICING_SYNC_ERROR,
  MISCONFIGURATION: GENERIC_ONLINE_PRICING_SYNC_ERROR,
  AdyenSplitSyncInvariant: GENERIC_ONLINE_PRICING_SYNC_ERROR,
  ADYEN_SPLIT_CONFIG_NO_RULES: GENERIC_ONLINE_PRICING_SYNC_ERROR,
  INCOMPLETE_ONLINE_PRICING:
    "Online pricing must include all four buckets. The change was not saved — please reopen the editor and try again.",
};

// Field-level validation: an out-of-band fee. The backend uses code
// PRICING_OUT_OF_RANGE (currently only reaches the FE when serialized with its
// code — see isOnlinePricingRangeMessage for the message fallback).
const VALIDATION_ERROR_CODES = new Set<string>(["PRICING_OUT_OF_RANGE"]);

/** Maps a backend error `code` to friendly copy, falling back to the generic
 *  "not saved" message when the code is unknown or missing. */
export function getOnlinePricingSyncErrorMessage(code?: string | null): string {
  if (code && SYNC_ERROR_MESSAGES[code]) {
    return SYNC_ERROR_MESSAGES[code];
  }
  return GENERIC_ONLINE_PRICING_SYNC_ERROR;
}

/** True when the error `code` indicates a field-level pricing validation error
 *  (e.g. out-of-range fee) whose message should be attached to the field. */
export function isOnlinePricingValidationError(code?: string | null): boolean {
  return !!code && VALIDATION_ERROR_CODES.has(code);
}

/** Fallback detector for the out-of-range validation error: the backend throws
 *  it via a shared error class whose `code` is currently dropped on
 *  serialization, so recognise it by its (own, non-Adyen) message shape too. */
export function isOnlinePricingRangeMessage(message?: unknown): boolean {
  return (
    typeof message === "string" &&
    /outside the allowed range|must be <=? max/i.test(message)
  );
}

/** Keep a short, self-contained backend validation message; otherwise fall back
 *  to a generic range message so we never echo raw upstream/stack text onto a
 *  field. */
export function safeValidationMessage(message?: unknown): string {
  if (typeof message === "string" && message.trim() && message.length <= 200) {
    return message;
  }
  return "This value is outside the allowed range for this partner.";
}

// The pricing endpoint's own refusals of a payload: a per-bucket set that does
// not validate, a row outside its group's partner band or in the wrong unit, or
// a payload in the other structure's shape. All are pre-write — nothing is
// persisted — so they belong on the form, not in a "try again" toast.
const PRICING_PAYLOAD_ERROR_CODES = new Set<string>([
  "INVALID_ONLINE_PRICING",
  "PRICING_OUT_OF_RANGE",
  "PRICING_VALUETYPE_MISMATCH",
  "ONLINE_PRICING_STRUCTURE_DOWNGRADE",
]);

export function isOnlinePricingPayloadError(code?: string | null): boolean {
  return !!code && PRICING_PAYLOAD_ERROR_CODES.has(code);
}

/** True when the pricing write failed because Adyen's split was not updated.
 *  Nothing was persisted; show the generic not-saved copy, never upstream text. */
export function isAdyenOnlinePricingSyncError(code?: string | null): boolean {
  return !!code && ADYEN_SYNC_ERROR_CODES.has(code);
}

/** The error body behind a rejected request. getTokenizedAxios rejects with the
 *  response body itself, so `{ message, code }` sits at the top level; a failure
 *  that never reached that interceptor still nests it under `response.data`, and
 *  reading only one of the two is how a real backend message gets replaced by a
 *  generic one.
 *
 *  A thrown Error is not a body — its message is ours, not the backend's, and
 *  putting a TypeError in front of an operator explains nothing. */
export function pricingErrorBody(err: unknown): {
  message?: string;
  code?: string;
} {
  const nested = (err as { response?: { data?: unknown } } | undefined)
    ?.response?.data;
  if (!nested && err instanceof Error) return {};
  const body = (nested ?? err ?? {}) as { message?: unknown; code?: unknown };
  return {
    message: typeof body.message === "string" ? body.message : undefined,
    code: typeof body.code === "string" ? body.code : undefined,
  };
}

// The online-pricing bucket keys, in display order. Single source of truth
// shared by the editor and the field-error resolver.
export const ONLINE_PRICING_BUCKET_KEYS = [
  "iDeal",
  "amex",
  "corporateCreditCards",
  "nonCorporateCreditCards",
  "appleGooglePay",
] as const;

export type OnlinePricingBucketKey =
  (typeof ONLINE_PRICING_BUCKET_KEYS)[number];

function collectFieldNames(errorBody: any): string[] {
  const names: string[] = [];
  if (Array.isArray(errorBody?.invalidFields)) {
    errorBody.invalidFields.forEach((f: any) => {
      if (f && typeof f.name === "string") names.push(f.name);
      else if (typeof f === "string") names.push(f);
    });
  }
  if (typeof errorBody?.field === "string") names.push(errorBody.field);
  // The backend range error only ships a message like
  // "business.onlinePricing.amex.value (7) is outside the allowed range …";
  // parse it too so the field still resolves without a structured field.
  if (typeof errorBody?.message === "string") names.push(errorBody.message);
  return names;
}

// Split into lowercased alphanumeric tokens so an exact bucket-key token can be
// matched inside dotted/bracketed paths or a free-text message.
function toSegments(name: string): string[] {
  return name
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** Resolve which online-pricing bucket(s) a field-level validation error refers
 *  to, from any structured info (`invalidFields[].name` / `field`) or the error
 *  message. Matches an EXACT token, so bucket keys that are substrings of one
 *  another (`corporateCreditCards` ⊂ `nonCorporateCreditCards`) never collide.
 *
 *  `keys` is the key set the merchant is actually priced on — a 16-bucket
 *  merchant's groups are not the legacy buckets, and looking for the wrong ones
 *  would leave the error unattached to any field. */
export function resolveInvalidOnlinePricingBuckets<T extends string>(
  errorBody: any,
  keys: readonly T[] = ONLINE_PRICING_BUCKET_KEYS as readonly string[] as readonly T[]
): T[] {
  const segments = collectFieldNames(errorBody).flatMap(toSegments);
  return keys.filter((key) => segments.includes(key.toLowerCase()));
}
